import { useLanguage, t } from "../../../i18n";
import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, Button, Form, Alert, Spinner } from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ROUTES from "../../../config/routes";
import "./Login.css";
import Logo from "../../../components/common/Logo/Logo";
import requestLoginOtp from "../../../features/auth/api/requestLoginOtp";
import verifyLoginOtp from "../../../features/auth/api/verifyLoginOtp";
import resendLoginOtp from "../../../features/auth/api/resendLoginOtp";
import { getApiValidationErrors, getLoginOtpRequestErrorMessage, getVerifyOtpErrorMessage, getResendOtpErrorMessage } from "../../../utils/apiErrorHandler";
import { useAuth } from "../../../features/auth/context/AuthContext";
import useDocumentTitle from "../../../hooks/useDocumentTitle";
const RESEND_SECONDS = 120;
const initialValues = {
  email: "",
  otp: ""
};
const validationSchema = Yup.object({
  email: Yup.string().email("Enter a valid email address").required("Email Address is required"),
  otp: Yup.string().when("$otpSent", {
    is: true,
    then: schema => schema.matches(/^\d{6}$/, "OTP must be exactly 6 digits").required("OTP is required"),
    otherwise: schema => schema.notRequired()
  })
});
const backendToFormikFieldMap = {
  email: "email",
  otp: "otp",
  purpose: "otp"
};
export default function Login() {
  useLanguage();
  useDocumentTitle("Login");
  const [otpSent, setOtpSent] = useState(false);
  const [apiError, setApiError] = useState("");
  const [apiSuccess, setApiSuccess] = useState("");
  const [resendSecondsLeft, setResendSecondsLeft] = useState(0);
  const [isResending, setIsResending] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const navigate = useNavigate();
  const {
    setAuthenticatedSession
  } = useAuth();
  useEffect(() => {
    let timer;
    if (otpSent && resendSecondsLeft > 0) {
      timer = setInterval(() => {
        setResendSecondsLeft(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [otpSent, resendSecondsLeft]);
  const handleSendOtp = async (email, setFieldError) => {
    setApiError("");
    setApiSuccess("");
    setIsSendingOtp(true);
    try {
      const response = await requestLoginOtp(email);
      if (response?.success === false) {
        setApiError("We could not continue with login right now. Please try again.");
        return;
      }
      setOtpSent(true);
      setResendSecondsLeft(RESEND_SECONDS);
      setApiSuccess(response?.message || "A verification code has been sent to your email.");
    } catch (error) {
      const validationErrors = getApiValidationErrors(error);
      if (Object.keys(validationErrors).length > 0) {
        Object.entries(validationErrors).forEach(([field, message]) => {
          const formikField = backendToFormikFieldMap[field] || field;
          setFieldError(formikField, Array.isArray(message) ? message[0] : message);
        });
      } else {
        setApiError(getLoginOtpRequestErrorMessage(error));
      }
    } finally {
      setIsSendingOtp(false);
    }
  };
  const handleSubmit = async (values, {
    setSubmitting,
    setFieldError
  }) => {
    setApiError("");
    setApiSuccess("");
    try {
      const response = await verifyLoginOtp(values);
      if (response?.success === false) {
        setApiError("We could not verify your code right now. Please try again.");
        return;
      }
      const token = response?.data?.token || "";
      const user = response?.data?.user || null;
      const normalizedEmail = user?.email || String(values?.email || "").trim().toLowerCase();
      setAuthenticatedSession({
        email: normalizedEmail,
        token,
        user
      });
      toast.success(t(response?.message || "Login successful."));
      if (user?.profile_status === "complete") {
        navigate(ROUTES.HOME, {
          replace: true
        });
      } else {
        navigate(ROUTES.COMPLETE_PROFILE, {
          replace: true
        });
      }
    } catch (error) {
      const validationErrors = getApiValidationErrors(error);
      if (Object.keys(validationErrors).length > 0) {
        Object.entries(validationErrors).forEach(([field, message]) => {
          const formikField = backendToFormikFieldMap[field] || field;
          setFieldError(formikField, Array.isArray(message) ? message[0] : message);
        });
      } else {
        setApiError(getVerifyOtpErrorMessage(error));
      }
    } finally {
      setSubmitting(false);
    }
  };
  const handleResendOtp = async email => {
    if (!email || resendSecondsLeft > 0 || isResending) return;
    setApiError("");
    setApiSuccess("");
    setIsResending(true);
    try {
      const response = await resendLoginOtp(email);
      if (response?.success === false) {
        setApiError("We could not resend the verification code right now. Please try again.");
        return;
      }
      setResendSecondsLeft(RESEND_SECONDS);
      setApiSuccess(response?.message || "A new verification code has been sent to your email.");
    } catch (error) {
      setApiError(getResendOtpErrorMessage(error));
    } finally {
      setIsResending(false);
    }
  };
  const handleChangeEmail = resetForm => {
    setApiError("");
    setApiSuccess("");
    setOtpSent(false);
    setResendSecondsLeft(0);
    setIsResending(false);
    setIsSendingOtp(false);
    resetForm({
      values: {
        email: "",
        otp: ""
      }
    });
  };
  return <div className="bb-auth-page">
      <Container>
        <Row className="justify-content-center">
          <Col lg={6} xl={5}>
            <div className="bb-auth-page__brand">
              <div className="bb-auth-logo">
                <Logo />
              </div>
            </div>

            <Card className="bb-auth-card">
              <Card.Body>
                <div className="bb-auth-card__header">
                  <h1 className="bb-auth-card__title">{t("Login with Email OTP")}</h1>
                  <p className="bb-auth-card__subtitle">{t("Enter your registered email address and verify using the 6-digit OTP.")}


                  </p>
                </div>

                {apiError ? <Alert variant="danger" className="mb-3">
                    {t(apiError)}
                  </Alert> : null}

                {apiSuccess ? <Alert variant="success" className="mb-3">
                    {t(apiSuccess)}
                  </Alert> : null}

                <Formik initialValues={initialValues} validationSchema={validationSchema} validationContext={{
                otpSent
              }} onSubmit={handleSubmit}>

                  {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                  validateField,
                  setFieldError,
                  resetForm,
                  isSubmitting
                }) => <Form onSubmit={handleSubmit}>
                      <Row className="g-4">
                        <Col xs={12}>
                          <Form.Group>
                            <Form.Label>{t("Email Address")}</Form.Label>
                            <Form.Control type="email" name="email" value={values.email} onChange={handleChange} onBlur={handleBlur} isInvalid={touched.email && !!errors.email} placeholder={t("Enter your email address")} disabled={otpSent} />

                            <Form.Control.Feedback type="invalid">
                              {t(errors.email)}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>

                        {!otpSent ? <Col xs={12}>
                            <Button type="button" className="w-100 bb-auth-card__submit-btn bb-brand-btn-outline" disabled={isSendingOtp} onClick={async () => {
                        await validateField("email");
                        const emailValue = values.email?.trim();
                        const emailHasError = !emailValue || !!errors.email;
                        if (emailHasError) return;
                        await handleSendOtp(emailValue, setFieldError);
                      }}>

                              {isSendingOtp ? <>
                                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />{t("Sending OTP...")}


                          </> : t("Send OTP")}
                            </Button>
                          </Col> : null}

                        {otpSent ? <>
                            <Col xs={12}>
                              <Form.Group>
                                <Form.Label>{t("6-Digit OTP")}</Form.Label>
                                <Form.Control type="text" inputMode="numeric" name="otp" value={values.otp} onChange={handleChange} onBlur={handleBlur} isInvalid={touched.otp && !!errors.otp} placeholder={t("Enter 6-digit OTP")} maxLength={6} />

                                <Form.Control.Feedback type="invalid">
                                  {t(errors.otp)}
                                </Form.Control.Feedback>
                              </Form.Group>

                              <div className="mt-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <small className="text-muted">
                                  {t(resendSecondsLeft > 0 ? `Resend available in ${resendSecondsLeft}s` : "Didn’t receive the code?")}
                                </small>

                                <Button type="button" variant="link" className="p-0 bb-auth-card__link" disabled={resendSecondsLeft > 0 || isResending} onClick={() => handleResendOtp(values.email)}>

                                  {t(isResending ? "Resending..." : "Resend OTP")}
                                </Button>
                              </div>
                            </Col>

                            <Col xs={12}>
                              <div className="text-center">
                                <Button type="button" variant="link" className="p-0 bb-auth-card__link" onClick={() => handleChangeEmail(resetForm)}>{t("Change Email")}


                            </Button>
                              </div>
                            </Col>

                            <Col xs={12}>
                              <Button type="submit" className="w-100 bb-auth-card__submit-btn" disabled={isSubmitting || isSendingOtp}>

                                {isSubmitting ? <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />{t("Verifying...")}


                            </> : t("Verify & Login")}
                              </Button>
                            </Col>
                          </> : null}
                      </Row>
                    </Form>}
                </Formik>

                <div className="bb-auth-card__footer">
                  <div className="mb-2">
                    <Button type="button" variant="link" className="p-0 bb-auth-card__link" onClick={() => navigate(ROUTES.HOME)}>{t("Go Back to Home")}


                    </Button>
                  </div>{t("Don’t have an account?")}

                  {t(" ")}
                  <Link to={ROUTES.REGISTER} className="bb-auth-card__link">{t("Register")}

                  </Link>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>;
}
